/**
 * Kleinteile NUR des Kapitels „Umsatz & Bezahlqualität" (P2-V3 § 2.0): Kartenkopf mit Zeitraum und Datenstand,
 * Bezahlquote mit Grau „jung" und ▲/▼ Zeitraum gegen Zeitraum, Veränderung mit Zerlegung, Menügruppen der Spaltenwahl.
 * Geteilte Bausteine kommen aus `components/auswertungen/kennzahlTeile.tsx`; hier liegt nur, was die P2-Karten
 * gemeinsam brauchen und P1/P3 nicht.
 */
import { FC, ReactNode } from 'react';
import { Box, Tooltip } from '@mui/material';
import InfoTip from '../../../components/InfoTip';
import { AbweichungMarke, formatDelta, int, spanne, Teile, VeraenderungWert } from '../../../components/auswertungen/kennzahlTeile';
import type { JungSchwellen, Veraenderung, ZeitraumTest } from '../../../helpers/auswertungenData';
import { formatBerlinWallclock } from '../../../helpers/auswertungenData';
import { formatPct, jungTip, TERMS } from '../../../helpers/kpiDefinitions';

/** Reihenfolge der Menügruppen (P2-V3 § 5, Reihenfolge = Bedeutung). */
export const P2_GRUPPEN = ['Menge', 'Wert', 'Trend', 'Zahlung', 'Kundentyp', 'Rechnungsstufen', 'Preis und Bindung', 'Modell'] as const;

/** Schwellen aus der API, ohne Antwort die Werte vom 25.10.2026 (nur bis die Antwort da ist). */
export const schwellenAus = (s: JungSchwellen | null | undefined): Required<JungSchwellen> => ({
  jung_tage: s?.jung_tage ?? 60,
  jung_anteil: s?.jung_anteil ?? 0.2,
  reife_tage: s?.reife_tage ?? 60,
});

/** `2026-10-22 22:20:03` → „22.10.2026, 22:20". */
export const standText = (ds: string | null | undefined): string => (ds ? formatBerlinWallclock(ds).slice(0, 17) : '–');

/** Datetime-Ende (exklusiv) → letzter gezählter Tag `YYYY-MM-DD`. */
export const letzterTag = (bisExkl: string): string => {
  const d = new Date(`${bisExkl.slice(0, 10)}T00:00:00Z`);
  if (bisExkl.length > 10 && bisExkl.slice(11) !== '00:00:00') return bisExkl.slice(0, 10);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

/** Kartenkopf (P2-V3 § 2.0/5): Zeitraum und Datenstand als eine Zeile über dem Inhalt. */
export const Kartenkopf: FC<{ children: ReactNode }> = ({ children }) => (
  <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5, lineHeight: 1.5 }}>{children}</Box>
);

/** „… bis 22.10., 22:20 (Datenstand Bestellsystem)". */
export const DatenstandText: FC<{ ds: string | null | undefined }> = ({ ds }) => (
  <>
    {standText(ds)} (
    <Tooltip title={TERMS.datenstandBestellsystem.tip} arrow placement="top">
      <Box component="span" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
        {TERMS.datenstandBestellsystem.label}
      </Box>
    </Tooltip>
    )
  </>
);

/** Zeitraum „01.01.–22.10.2026" aus Datetime-Grenzen. */
export const zeitraumText = (von: string, bisExkl: string): string => spanne(von.slice(0, 10), letzterTag(bisExkl));

/**
 * Bezahlquote mit „x von y", grau mit Grund, wenn die Zeile jung ist (Regel 3), und ▲/▼ Zeitraum gegen Zeitraum
 * (Regel 8, Tooltip `abweichungZeitraumMarke`). Unter 20 reifen Anfos ebenfalls grau (wenig Anfos).
 */
export const QuoteZelle: FC<{ rate: number | null; bezahlt: number; reif: number; jung?: boolean; test?: ZeitraumTest | null; tipZusatz?: string }> = ({
  rate,
  bezahlt,
  reif,
  jung,
  test,
  tipZusatz,
}) => {
  if (rate === null) return <>—</>;
  const grau = jung || reif < 20;
  return (
    <Box sx={{ color: grau ? 'text.disabled' : 'inherit', lineHeight: 1.25 }}>
      {formatPct(rate)}
      <AbweichungMarke marke={test?.flag ?? null} tip={`${TERMS.abweichungZeitraumMarke.tip}${tipZusatz ? ` ${tipZusatz}` : ''}`} />
      <Box component="span" sx={{ display: 'block', fontSize: 11, color: grau ? 'inherit' : 'text.secondary' }}>
        {int(bezahlt)} von {int(reif)}
      </Box>
    </Box>
  );
};

/** Grund der grauen Quote (Spalte `unsicher`): „jung" mit den Schwellen der API, sonst wenig Anfos. */
export const jungGrund = (jung: boolean | undefined, s: Required<JungSchwellen>): string | null => (jung ? jungTip(s.jung_anteil, s.jung_tage) : null);

/**
 * Veränderung „+28,6 %" mit Zerlegung „Anfos +20,3 % · Ø je Anfo +6,9 %" als Unterzeile (Regel „Veränderung heißt,
 * was sie ist"). `zerlegung: false` (Handy) → Zerlegung nur im Tooltip. `null` → „—" mit Grund.
 */
export const VeraenderungZelle: FC<{ v: Veraenderung | null | undefined; ohneGrund: string; zerlegung?: boolean; tipZusatz?: string }> = ({
  v,
  ohneGrund,
  zerlegung = true,
  tipZusatz,
}) => {
  if (!v) {
    return (
      <Tooltip title={ohneGrund} arrow placement="top">
        <Box component="span" sx={{ cursor: 'help' }}>
          —
        </Box>
      </Tooltip>
    );
  }
  return (
    <Box sx={{ lineHeight: 1.25, color: v.grau_grund ? 'text.disabled' : 'inherit' }}>
      <VeraenderungWert v={v} schwelle={20} />
      {tipZusatz && <InfoTip text={tipZusatz} />}
      {zerlegung && v.delta !== null && (
        <Teile
          teile={[`Anfos ${formatDelta(v.delta_anfos, 1)}`, `Ø je Anfo ${formatDelta(v.delta_je_anfo, 1)}`]}
          sx={{ fontSize: 11, color: v.grau_grund ? 'inherit' : 'text.secondary', whiteSpace: 'normal' }}
        />
      )}
    </Box>
  );
};

/** Summe gegen Summe (für den Tooltip der Veränderung): Unterschied zur Rechenregel, aus den API-Zahlen. */
export const summeGegenSumme = (jetzt: number, basis: number): number | null => (basis > 0 ? (jetzt / basis - 1) * 100 : null);
